import React, { useCallback, Fragment, ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import LimitedPageContainer from '../../../../../components/limited-page-block/limited-page-container.component';
import PageTabs from '../../../../../components/page-tabs/page-tabs.component';
import SettingsUserSummary from './settings-user-summary/settings-user-summary.component';
import SettingsUserPreferences from './settings-user-preferences/settings-user-preferences.component';
import FormatService from '../../../../../services/format-service';
import { ROUTE_SETTINGS_PROFILE } from '../../../../../constants/system/routes';
import { QUERY_PARAM_TAB } from '../../../../../constants/system/query-params';
import { TAB_NAME_PREFERENCES, TAB_NAME_SUMMARY } from '../../../../../constants/system/tab-names';

const SettingsUserGeneral: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isEditMode,
    isFormChanged,
    isSaveDisabled,
    isUserProfile,
    isRightDrawerOpen,
    formError,
    userData,
    assetTypeList,
    roleList,
    contactCompanyList,
    portfolioList,
    routeList,
    selectedDefaultPortfolios,
    selectedSearchResultGroups,
    onCancelForm,
    onSubmitForm,
    onPhoneChange,
    onPhoneWorkChange,
    onAssetTypesChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onVerifyPhone,
    onBeforeLocationChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onDefaultPortfolioValuesChange,
    onSearchResultGroupValuesChange,
  } = props;

  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <SettingsUserSummary
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          isUserProfile={isUserProfile}
          formError={formError}
          userData={userData}
          roleList={roleList}
          assetTypeList={assetTypeList}
          contactCompanyList={contactCompanyList}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onPhoneChange={onPhoneChange}
          onPhoneWorkChange={onPhoneWorkChange}
          onAssetTypesChange={onAssetTypesChange}
          onVerifyPhone={onVerifyPhone}
          onContactCompanySearch={onContactCompanySearch}
          onContactCompanySearchClose={onContactCompanySearchClose}
          onAddressSearchCompleted={onAddressSearchCompleted}
          onAddressFieldKeyDown={onAddressFieldKeyDown}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_PREFERENCES,
      title: 'Preferences',
      component: (
        <SettingsUserPreferences
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          formError={formError}
          userData={userData}
          routeList={routeList}
          portfolioList={portfolioList}
          selectedDefaultPortfolios={selectedDefaultPortfolios}
          selectedSearchResultGroups={selectedSearchResultGroups}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDefaultPortfolioValuesChange={onDefaultPortfolioValuesChange}
          onSearchResultGroupValuesChange={onSearchResultGroupValuesChange}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: ROUTE_SETTINGS_PROFILE,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs]);

  const tabItemSummary: TabItem | null = tabs && tabs.length ? tabs.find((tab: TabItem): boolean => (
    tab.name === TAB_NAME_SUMMARY
  )) || null : null;
  const tabItemSummaryComponent: ReactElement | null = tabItemSummary && tabItemSummary.component
    ? tabItemSummary.component : null;

  return (
    <Fragment>
      {
        userData && (
          isUserProfile ? (
            <LimitedPageContainer isActive>
              <PageTabs
                isDisabled={isFormChanged}
                isCompressed={isRightDrawerOpen}
                tabs={tabs}
                currentTab={queryTab}
                onTabChange={onTabChange}
                onBeforeLocationChange={onBeforeLocationChange}
              />
            </LimitedPageContainer>
          ) : (
            tabItemSummaryComponent
          )
        )
      }
    </Fragment>
  );
});

export default SettingsUserGeneral;
